import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { shouldRetryRequest } from "api/apiError";
import { billingKeys } from "api/billing";
import {
  Client,
  ClientLifecycleStatus,
  ClientPayload,
  ClientStage,
  clientKeys,
  createClient,
  deleteClient,
  fetchClients,
  setClientStage,
  setClientStatus,
  stageOf,
  updateClient,
} from "api/clients";
import { fleetKeys } from "api/fleet";
import { isConnected } from "Components/Tenants";
import { notify } from "Components/ui";
import { addClientToRecycleBin } from "utils/recycleBin";
import { useBillingOverviewQuery } from "Routes/Billing/data";
import { ClientBillingMap } from "./types";

/** Billing status per client for the Clients table; only fetched with billing/read. */
export const useClientBillingStatuses = (enabled: boolean) => {
  const query = useBillingOverviewQuery(enabled);
  const billingByClient = React.useMemo<ClientBillingMap>(() => {
    const map: ClientBillingMap = {};
    (query.data ? query.data.rows : []).forEach((row) => {
      map[row.tenantId] = row;
    });
    return map;
  }, [query.data]);
  return {
    billingByClient,
    billingLoading: enabled && query.isLoading,
    billingFailed: enabled && query.isError,
  };
};

const EMPTY: Client[] = [];

const matches = (client: Client, term: string): boolean => {
  if (!term) {
    return true;
  }
  const haystack = [
    client.name,
    client.slug,
    client.uri,
    client.deployment ? `${client.deployment.server} ${client.deployment.app}` : "",
    ...(client.contacts || []).map((contact) => `${contact.name || ""} ${contact.email || ""}`),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(term);
};

/** Clients split into Actionable (beta) and Locked (until production release), filtered by search. */
const useClientsData = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = React.useState<string>("");

  const query = useQuery({
    queryKey: clientKeys.list(),
    queryFn: fetchClients,
    retry: shouldRetryRequest,
    staleTime: 30 * 1000,
  });
  const clients = query.data || EMPTY;

  const groups = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    // Connected clients first (the others are greyed out); the server already sorts by name.
    const visible = clients
      .filter((client) => matches(client, term))
      .sort((a, b) => Number(isConnected(b.link)) - Number(isConnected(a.link)));
    return {
      actionable: visible.filter((client) => stageOf(client) === "beta"),
      locked: visible.filter((client) => stageOf(client) === "locked"),
    };
  }, [clients, search]);

  const counts = React.useMemo(
    () => ({
      total: clients.length,
      actionable: clients.filter((client) => stageOf(client) === "beta").length,
      locked: clients.filter((client) => stageOf(client) === "locked").length,
      connected: clients.filter((client) => isConnected(client.link)).length,
    }),
    [clients]
  );

  // Client changes show up in billing rows and the Dashboard too.
  const refreshAll = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: clientKeys.all }),
      queryClient.invalidateQueries({ queryKey: billingKeys.overview() }),
      queryClient.invalidateQueries({ queryKey: fleetKeys.all }),
    ]);
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: (payload: ClientPayload) => createClient(payload),
    onSuccess: async (client) => {
      notify.success(`${client.name} (${client.slug}) added to Locked`);
      await refreshAll();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<ClientPayload> }) =>
      updateClient(id, payload),
    onSuccess: async (client) => {
      notify.success(`${client.name} saved`);
      await refreshAll();
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ClientLifecycleStatus }) =>
      setClientStatus(id, status),
    onSuccess: refreshAll,
    onError: (err) => notify.error(err),
  });

  const stageMutation = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: ClientStage }) => setClientStage(id, stage),
    onSuccess: async (client) => {
      notify.success(
        stageOf(client) === "beta"
          ? `${client.name} (${client.slug}) is now Actionable`
          : `${client.name} (${client.slug}) is now Locked`
      );
      await refreshAll();
    },
    onError: (err) => notify.error(err),
  });

  const deleteMutation = useMutation({
    mutationFn: (client: Client) => deleteClient(client._id),
    onSuccess: async (deleted) => {
      addClientToRecycleBin(deleted);
      notify.success(`${deleted.name} deleted. You can restore it from Settings → Recycle bin.`);
      await refreshAll();
    },
    onError: (err) => notify.error(err),
  });

  return {
    clients,
    groups,
    counts,
    search,
    setSearch,
    loading: query.isLoading,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
    fetching: query.isFetching,
    createMutation,
    updateMutation,
    statusMutation,
    stageMutation,
    deleteMutation,
  };
};

export default useClientsData;
